/**
 * Auditoria (redesign admin, Fase 5): trilha do event store
 * (`/api/admin/audit`), somente leitura. A rota responde 501 quando o store
 * não está ligado no ambiente: o cliente devolve `null` e a tela declara a
 * pendência em vez de mostrar "nenhum evento".
 */
import { createElement as h, useState, type ReactElement } from "react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import type { PlatformAuditEvent } from "../api/types.js";
import { ACTOR_LABELS, actorCounts, filterAuditEvents, type AuditActorFilter } from "../dashboard/audit-view.js";
import { formatCount, formatDateTime } from "../format.js";
import { useAuditTrail } from "../queries.js";
import { Card, EmptyState, Field, PageHeader, Segmented, controlClass } from "../ui/index.js";

/** Teto de linhas na tela: a trilha pode ser longa e a página não pagina. */
const MAX_ROWS = 200;

export function AuditoriaView({ events }: { events: PlatformAuditEvent[] | null }): ReactElement {
  const [query, setQuery] = useState("");
  const [actor, setActor] = useState<AuditActorFilter>("todos");
  const header = h(PageHeader, { title: "Auditoria", description: "Trilha de eventos do sistema: quem fez o quê e quando." });

  if (events === null) {
    return h(
      "div",
      { className: "page page--auditoria flex flex-col gap-6", "data-page": "auditoria" },
      header,
      h(StatusPanel, {
        kind: "awaiting_source",
        title: `Trilha de auditoria ${AWAITING_CONTRACT_LABEL}`,
        detail: "O event store não está ligado neste ambiente (a rota /api/admin/audit respondeu 501 ou não existe)."
      })
    );
  }

  const counts = actorCounts(events);
  const rows = filterAuditEvents(events, query, actor);
  return h(
    "div",
    { className: "page page--auditoria flex flex-col gap-6", "data-page": "auditoria" },
    header,
    h(
      "div",
      { className: "flex flex-wrap items-end gap-3" },
      h(Field, {
        label: "Buscar (evento, registro, ator ou correlação)",
        className: "min-w-[16rem] flex-1",
        children: (ids) =>
          h("input", {
            id: ids.id,
            type: "search",
            className: controlClass,
            value: query,
            "data-filter-control": "query",
            onChange: (e: { target: { value: string } }) => setQuery(e.target.value)
          })
      }),
      h(Segmented<AuditActorFilter>, {
        label: "Ator",
        value: actor,
        onChange: setActor,
        options: [
          { value: "todos", label: `Todos (${formatCount(events.length)})` },
          { value: "human", label: `${ACTOR_LABELS.human} (${formatCount(counts.human)})` },
          { value: "ai", label: `${ACTOR_LABELS.ai} (${formatCount(counts.ai)})` },
          { value: "system", label: `${ACTOR_LABELS.system} (${formatCount(counts.system)})` },
          { value: "customer", label: `${ACTOR_LABELS.customer} (${formatCount(counts.customer)})` }
        ]
      })
    ),
    h(
      "p",
      { className: "text-sm text-muted", "data-audit-count": rows.length },
      rows.length > MAX_ROWS
        ? `Mostrando os ${MAX_ROWS} mais recentes de ${formatCount(rows.length)} evento(s).`
        : `${formatCount(rows.length)} evento(s).`
    ),
    rows.length === 0
      ? h(EmptyState, { title: "Nenhum evento neste recorte" })
      : h(
          Card,
          null,
          h(
            "div",
            { className: "overflow-x-auto" },
            h(
              "table",
              { className: "w-full text-left text-sm", "data-table": "auditoria" },
              h(
                "thead",
                null,
                h(
                  "tr",
                  { className: "text-xs uppercase text-muted" },
                  ["Quando", "Evento", "Registro", "Ator", "Correlação"].map((title) =>
                    h("th", { key: title, scope: "col", className: "px-2 py-2" }, title)
                  )
                )
              ),
              h(
                "tbody",
                null,
                rows.slice(0, MAX_ROWS).map((event) =>
                  h(
                    "tr",
                    { key: event.id, "data-audit-event": event.id, className: "border-t border-border" },
                    h("td", { className: "whitespace-nowrap px-2 py-2 text-muted" }, formatDateTime(event.occurredAt)),
                    h("td", { className: "px-2 py-2 font-semibold" }, event.eventType),
                    h("td", { className: "px-2 py-2" }, `${event.aggregateType} · ${event.aggregateId}`),
                    h("td", { className: "px-2 py-2" }, `${ACTOR_LABELS[event.actorType]}${event.actorId ? ` · ${event.actorId}` : ""}`),
                    h("td", { className: "px-2 py-2 font-mono text-xs text-muted" }, event.correlationId)
                  )
                )
              )
            )
          )
        )
  );
}

export function Auditoria(): ReactElement {
  const query = useAuditTrail();
  if (query.isPending) return h(StatusPanel, { kind: "loading", title: "Carregando a trilha de auditoria…" });
  if (query.error) {
    return h(StatusPanel, { kind: "error", title: "Não foi possível carregar a trilha de auditoria", detail: errorDetail(query.error) });
  }
  return h(AuditoriaView, { events: query.data ?? null });
}
